import React from 'react';
import propTypes from 'prop-types';
import Tab from '@mui/material/Tab';
import ScrollableTabs from 'tailwind-components/ScrollableTabs/ScrollableTabs';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import { HOLD_RANKS } from 'constants/enum';

export const AGENT_TAB_IDS = {
  ALL: 'all',
  ACTIVE: 'active',
  HOLDS: 'holds',
  OFFBOARDING: 'offboarding',
  INACTIVE: 'inactive'
};

export function isHoldRank(rank) {
  return HOLD_RANKS.has(rank);
}

export function isActiveTabRank(rank) {
  if (isHoldRank(rank)) return false;
  if (rank === 'Inactive') return false;
  if (rank === 'Offboarding') return false;
  return true;
}

export function filterAgentsByTab(agents, tabId) {
  const list = agents ?? [];
  switch (tabId) {
    case AGENT_TAB_IDS.ALL:
      return list;
    case AGENT_TAB_IDS.ACTIVE:
      return list.filter(a => isActiveTabRank(a.rank));
    case AGENT_TAB_IDS.HOLDS:
      return list.filter(a => isHoldRank(a.rank));
    case AGENT_TAB_IDS.OFFBOARDING:
      return list.filter(a => a.rank === 'Offboarding');
    case AGENT_TAB_IDS.INACTIVE:
      return list.filter(a => a.rank === 'Inactive');
    default:
      return list;
  }
}

export function getAgentTabCounts(agents) {
  const list = agents ?? [];
  let active = 0;
  let holds = 0;
  let offboarding = 0;
  let inactive = 0;

  for (const a of list) {
    const r = a.rank;
    if (r === 'Inactive') inactive += 1;
    else if (isHoldRank(r)) holds += 1;
    else if (r === 'Offboarding') offboarding += 1;
    else active += 1;
  }

  return {
    all: list.length,
    active,
    holds,
    offboarding,
    inactive
  };
}

const OFFBOARDING_PURPLE = '#9333ea';
/** Matches AgentsTable StatusBadge active: text-green-700 / bg-green-100 */
const ACTIVE_GREEN = '#15803d';

function RankTabBadge({ badgeVariant, count, selected }) {
  const theme = useTheme();
  const hasActiveAccent = badgeVariant === 'active' && count > 0;
  const hasHoldsAccent = badgeVariant === 'holds' && count > 0;
  const hasOffboardingAccent = badgeVariant === 'offboarding' && count > 0;

  if (hasActiveAccent) {
    const bg = selected ? alpha(ACTIVE_GREEN, 0.22) : alpha(ACTIVE_GREEN, 0.12);
    return (
      <Chip
        label={count}
        size='small'
        sx={{
          height: 24,
          minWidth: 28,
          fontWeight: 600,
          fontSize: theme.typography.caption.fontSize,
          '& .MuiChip-label': { px: 1 },
          bgcolor: bg,
          color: ACTIVE_GREEN,
          border: 'none'
        }}
      />
    );
  }

  if (hasHoldsAccent) {
    const bg = selected
      ? alpha(theme.palette.error.main, 0.22)
      : alpha(theme.palette.error.main, 0.12);
    return (
      <Chip
        label={count}
        size='small'
        sx={{
          height: 24,
          minWidth: 28,
          fontWeight: 600,
          fontSize: theme.typography.caption.fontSize,
          '& .MuiChip-label': { px: 1 },
          bgcolor: bg,
          color: theme.palette.error.dark,
          border: 'none'
        }}
      />
    );
  }

  if (hasOffboardingAccent) {
    const bg = selected
      ? alpha(OFFBOARDING_PURPLE, 0.22)
      : alpha(OFFBOARDING_PURPLE, 0.12);
    return (
      <Chip
        label={count}
        size='small'
        sx={{
          height: 24,
          minWidth: 28,
          fontWeight: 600,
          fontSize: theme.typography.caption.fontSize,
          '& .MuiChip-label': { px: 1 },
          bgcolor: bg,
          color: OFFBOARDING_PURPLE,
          border: 'none'
        }}
      />
    );
  }

  return (
    <Chip
      label={count}
      size='small'
      sx={{
        height: 24,
        minWidth: 28,
        fontWeight: selected ? 600 : 500,
        fontSize: theme.typography.caption.fontSize,
        '& .MuiChip-label': { px: 1 },
        bgcolor: selected
          ? alpha(theme.palette.primary.main, 0.15)
          : theme.palette.grey[300],
        color: selected ? theme.palette.primary.dark : theme.palette.grey[700],
        border: 'none'
      }}
    />
  );
}

RankTabBadge.propTypes = {
  badgeVariant: propTypes.oneOf(['default', 'active', 'holds', 'offboarding'])
    .isRequired,
  count: propTypes.number.isRequired,
  selected: propTypes.bool.isRequired
};

function RankTabLabel({ labelText, badgeVariant, count, selected, loading }) {
  const theme = useTheme();

  return (
    <Stack
      direction='row'
      alignItems='center'
      justifyContent='center'
      flexWrap='nowrap'
      spacing={1}
      useFlexGap
      sx={{
        typography: 'body2',
        fontWeight: selected ? 600 : 500,
        whiteSpace: 'nowrap',
        color: selected
          ? theme.palette.primary.main
          : theme.palette.text.secondary
      }}
    >
      <span>{labelText}</span>
      {loading ? (
        <CircularProgress
          size={16}
          sx={{ color: 'inherit' }}
          aria-label={`Loading ${labelText} count`}
        />
      ) : (
        <RankTabBadge
          badgeVariant={badgeVariant}
          count={count}
          selected={selected}
        />
      )}
    </Stack>
  );
}

RankTabLabel.propTypes = {
  labelText: propTypes.string.isRequired,
  badgeVariant: propTypes.oneOf(['default', 'active', 'holds', 'offboarding'])
    .isRequired,
  count: propTypes.number.isRequired,
  selected: propTypes.bool.isRequired,
  loading: propTypes.bool
};

export default function AgentRankTabs({
  activeTabId,
  onTabChange,
  counts,
  offboardingLoading = false,
  loading = false
}) {
  const items = [
    {
      id: AGENT_TAB_IDS.ACTIVE,
      label: 'Active',
      count: counts.active,
      badgeVariant: 'active'
    },
    {
      id: AGENT_TAB_IDS.HOLDS,
      label: 'Holds',
      count: counts.holds,
      badgeVariant: 'holds'
    },
    {
      id: AGENT_TAB_IDS.OFFBOARDING,
      label: 'Offboarding',
      count: counts.offboarding,
      badgeVariant: 'offboarding',
      loading: offboardingLoading
    },
    {
      id: AGENT_TAB_IDS.INACTIVE,
      label: 'Inactive',
      count: counts.inactive,
      badgeVariant: 'default'
    },
    {
      id: AGENT_TAB_IDS.ALL,
      label: 'All agents',
      count: counts.all,
      badgeVariant: 'default'
    }
  ];

  const handleChange = (_event, nextId) => {
    onTabChange(nextId);
  };

  return (
    <ScrollableTabs
      value={activeTabId}
      onChange={handleChange}
      data-tour='agency-rank-tabs'
    >
      {items.map(item => (
        <Tab
          key={item.id}
          value={item.id}
          disabled={loading}
          label={
            <RankTabLabel
              labelText={item.label}
              badgeVariant={item.badgeVariant}
              count={item.count}
              selected={activeTabId === item.id}
              loading={loading || Boolean(item.loading)}
            />
          }
        />
      ))}
    </ScrollableTabs>
  );
}

AgentRankTabs.propTypes = {
  activeTabId: propTypes.string.isRequired,
  onTabChange: propTypes.func.isRequired,
  counts: propTypes.shape({
    all: propTypes.number,
    active: propTypes.number,
    holds: propTypes.number,
    offboarding: propTypes.number,
    inactive: propTypes.number
  }).isRequired,
  offboardingLoading: propTypes.bool,
  loading: propTypes.bool
};
